// Kid-device modal — parent UI for setting up / resetting a household's
// shared kids login (one Auth0 account per household, typed into the
// family tablet once).
//
// Two modes:
//   - "provision": create the shared account the first time.
//   - "reset": forgot the password (≥30d inactivity or yearly expiry).
//
// The chosen password never leaves the parent's browser → mirror-agent
// → Auth0. Parent copies it and types it into the tablet during setup;
// refresh-token rotation handles every subsequent silent re-auth.

import { useEffect, useMemo, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api } from "../../lib/rpc";

interface KidDeviceModalProps {
  open: boolean;
  mode: "provision" | "reset";
  householdId: number;
  email: string; // synthetic email shown read-only — same on both modes
  onClose: () => void;
  onSuccess: () => void;
  notify: (msg: string) => void;
}

// Auth0 + mirror-agent's auth.mjs both require ≥10 char passwords.
// base64url (no '+' or '/') keeps it clipboard-safe and easy to type on
// an Android keyboard.
function generateStrongPassword(): string {
  const buf = new Uint8Array(18);
  crypto.getRandomValues(buf);
  let s = "";
  for (const b of buf) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Synthetic-email domain for the live preview. The server is authoritative
// (AUTH0_KID_EMAIL_DOMAIN); this just renders <handle>@... as the parent types.
const KID_EMAIL_DOMAIN = "kid.beemerworks.com";
// Mirror of the server's DEVICE_HANDLE_RE in auth0_mgmt.mjs.
const HANDLE_RE = /^[a-z0-9][a-z0-9-]{0,28}[a-z0-9]$/;

export function KidDeviceModal({ open, mode, householdId, email, onClose, onSuccess, notify }: KidDeviceModalProps) {
  const [handle, setHandle] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setHandle("");
    setPassword("");
    setReveal(false);
    setSaving(false);
    setCopied(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !saving) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, saving]);

  const handleValid = mode !== "provision" || HANDLE_RE.test(handle);
  const canSave = password.length >= 10 && handleValid && !saving;
  const title = useMemo(() => (
    mode === "provision" ? "Set up shared kids login" : "Reset shared kids login password"
  ), [mode]);

  if (!open) return null;

  const onGenerate = () => {
    const pw = generateStrongPassword();
    setPassword(pw);
    setReveal(true);
    setCopied(false);
  };

  const onCopy = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      notify("Couldn't copy — select the password manually");
    }
  };

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      if (mode === "provision") {
        await api.householdsProvisionKidDevice(householdId, handle, password);
        notify("Shared kids login created");
      } else {
        await api.householdsResetKidDevicePassword(householdId, password);
        notify("Shared login password reset");
      }
      onSuccess();
      onClose();
    } catch (err) {
      notify((err as Error)?.message || "save failed");
      setSaving(false);
    }
  };

  return (
    <div className="confirm-bg" onClick={() => { if (!saving) onClose(); }}>
      <div className="add-kid-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <header className="add-kid-head">
          <h3>{title}</h3>
          <button className="icon-btn" onClick={onClose} title="Close" type="button" disabled={saving}>
            <Icon name="x" size={13} />
          </button>
        </header>

        <p className="add-kid-sub">
          {mode === "provision"
            ? "This is the one login the family types into the tablet. After signing in, kids pick their own profile (and enter a PIN if you set one). The app stays signed in automatically until 30 days of no use (or one year, whichever comes first)."
            : "Pick a new password and re-enter it on the tablet. The next refresh (or relaunch) picks up the new credentials."}
        </p>

        <div className="add-kid-body">
          {mode === "provision" ? (
            <div className="add-kid-row">
              <label className="add-kid-label">Login handle</label>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  className="add-kid-input"
                  value={handle}
                  onInput={(e) => setHandle((e.currentTarget as HTMLInputElement).value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  placeholder="e.g. beemers"
                  maxLength={30}
                  autoFocus
                  spellcheck={false}
                  style={{ flex: 1 }}
                />
                <span style={{ color: "var(--text-dim)", fontSize: 13, whiteSpace: "nowrap" }}>@{KID_EMAIL_DOMAIN}</span>
              </div>
              <div className="add-kid-hint">
                {handle && !handleValid
                  ? <span className="add-kid-hint-warn">2-30 chars, no leading/trailing hyphen</span>
                  : <>Signs in as <b>{(handle || "…")}@{KID_EMAIL_DOMAIN}</b></>}
              </div>
            </div>
          ) : (
            <div className="add-kid-row">
              <label className="add-kid-label">Email</label>
              <input
                className="add-kid-input"
                value={email}
                readOnly
                tabIndex={-1}
                style={{ opacity: 0.7 }}
              />
            </div>
          )}

          <div className="add-kid-row">
            <label className="add-kid-label">Password</label>
            <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
              <input
                className="add-kid-input"
                value={password}
                onInput={(e) => { setPassword((e.currentTarget as HTMLInputElement).value); setCopied(false); }}
                type={reveal ? "text" : "password"}
                placeholder="min 10 characters"
                autoComplete="new-password"
                spellcheck={false}
                style={{ flex: 1 }}
              />
              <button
                className="manage-tags-done"
                type="button"
                onClick={() => setReveal(!reveal)}
                title={reveal ? "Hide" : "Show"}
                style={{ padding: "0 10px", minWidth: 56 }}
              >
                {reveal ? "Hide" : "Show"}
              </button>
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
              <button className="manage-tags-done" type="button" onClick={onGenerate}>
                <Icon name="sparkle" size={11} /> Generate strong
              </button>
              <button className="manage-tags-done" type="button" onClick={onCopy} disabled={!password}>
                <Icon name={copied ? "check" : "share"} size={11} /> {copied ? "Copied" : "Copy"}
              </button>
            </div>
            {password.length > 0 && password.length < 10 && (
              <div className="add-kid-hint add-kid-hint-warn">Password must be at least 10 characters</div>
            )}
          </div>
        </div>

        <footer className="add-kid-foot">
          <button className="manage-tags-done" type="button" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="manage-tags-add-btn" type="button" onClick={submit} disabled={!canSave}>
            {saving ? "Saving…" : (mode === "provision" ? "Create login" : "Reset password")}
          </button>
        </footer>
      </div>
    </div>
  );
}
